import { Endpoint, Graph, Api } from '@square/ignition';
import AccessTokenField from '@squareup/dex-feature-dex-tech-main-access-token-field';
import {
  ApiExplorerOasContainer,
  ApiExplorerInputContainer,
  ApiExplorerRequestRunnerContainer,
  ApiExplorerAccessTokenContainer,
  ApiExplorerFieldDropdownOptionsContainer,
  ApiExplorerFeedbackContainer,
} from '@squareup/dex-feature-dex-tech-main-api-explorer-container';
import { PromptFeedbackWidgetWrapper } from '@squareup/dex-feature-dex-tech-main-prompt-feedback-widget-wrapper';
import {
  ApiExplorerEndpointHeading,
  ApiExplorerFieldContainer,
  ApiExplorerRequestContentContainer,
  ApiExplorerResponseContentContainer,
  ApiExplorerSelectorContainer,
} from '@squareup/dex-feature-shared-api-explorer';
import { ReferenceObjectModalProvider } from '@squareup/dex-tech-reference-properties';
import { ReferenceHrefBuilders, toUrl } from '@squareup/dex-tech-utils-routing';
import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import {
  ApiExplorerContentLayout,
  ApiExplorerRequestResponseAsideLayout,
} from '@squareup/dex-ui-shared-api-explorer-page-layout';
import { toApiId } from '@squareup/dex-utils-oas';
import { useParams } from '@squareup/dex-utils-shared-routing-hooks';
import { ComponentIds } from '@squareup/svelte-dex-feedback';
import React, { FC, useMemo } from 'react';

interface Props {
  graph: Graph;
  endpoint: Endpoint;
  api: Api;
}

const ExplorerEndpointPage: FC<Props> = ({ graph, endpoint, api }) => {
  // TODO: Compose this page of
  // - Page selectors
  // - Access token
  // - Request/response views
  // - Input fields
  // - Request runner
  const query = useParams();
  const square_version = query?.['square_version'] as string;

  const supportedEnvironments = useMemo(
    () =>
      [ApplicationType.Sandbox, ApplicationType.Production].filter((env) =>
        endpoint.notSupportedInSandbox
          ? env === ApplicationType.Production
          : true
      ),
    [endpoint]
  );

  const endpointSummary = useMemo(
    () =>
      // Add the link as markdown so that it appears as a single paragraph and not two blocks
      `${endpoint?.summary || ''} [API Reference ->](${toUrl(
        ReferenceHrefBuilders.versionApiEndpoint({
          api: toApiId(api?.id),
          endpoint: endpoint.id,
          square_version,
        })
      )})`,
    [api?.id, endpoint.id, endpoint?.summary, square_version]
  );

  return (
    <ApiExplorerFeedbackContainer>
      <ReferenceObjectModalProvider>
        <ApiExplorerOasContainer graph={graph} endpoint={endpoint} api={api}>
          <ApiExplorerAccessTokenContainer>
            <ApiExplorerInputContainer>
              <ApiExplorerFieldDropdownOptionsContainer>
                <ApiExplorerRequestRunnerContainer>
                  <ApiExplorerContentLayout
                    apiSelectors={<ApiExplorerSelectorContainer />}
                    fieldContent={
                      <>
                        <ApiExplorerEndpointHeading
                          summary={endpointSummary}
                          endpoint={endpoint}
                        />
                        <AccessTokenField
                          supportedEnvironments={supportedEnvironments}
                        />
                        <ApiExplorerFieldContainer />
                      </>
                    }
                    requestResponseContent={
                      <ApiExplorerRequestResponseAsideLayout
                        requestContent={<ApiExplorerRequestContentContainer />}
                        responseContent={
                          <ApiExplorerResponseContentContainer />
                        }
                      />
                    }
                  />
                </ApiExplorerRequestRunnerContainer>
              </ApiExplorerFieldDropdownOptionsContainer>
            </ApiExplorerInputContainer>
          </ApiExplorerAccessTokenContainer>
        </ApiExplorerOasContainer>
      </ReferenceObjectModalProvider>

      <PromptFeedbackWidgetWrapper componentId={ComponentIds.tools} />
    </ApiExplorerFeedbackContainer>
  );
};

export { ExplorerEndpointPage };
